import { useApi } from '@backstage/frontend-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CHECKABLE_ENTITY_KINDS } from '@internal/backstage-plugin-pulse-common';
import useAsync from 'react-use/esm/useAsync';
import { SCOPE_FACETS } from '../components/scope-filters/scope-filter-model';

// Checks can only produce results for kinds the collectors gather facts for.
const CHECKABLE_KINDS = new Set(
  CHECKABLE_ENTITY_KINDS.map(kind => kind.toLowerCase()),
);

/**
 * Distinct annotation keys and, per key, the values present on the given
 * entities. The catalog facet API cannot enumerate annotation keys (a facet
 * path must be known up front), so the keys are aggregated from the entities.
 */
const collectAnnotationOptions = (
  entities: Entity[],
): Record<string, string[]> => {
  const byKey = new Map<string, Set<string>>();
  entities.forEach(entity => {
    Object.entries(entity.metadata.annotations ?? {}).forEach(
      ([key, value]) => {
        const values = byKey.get(key) ?? new Set<string>();
        byKey.set(key, values);
        if (value) {
          values.add(value);
        }
      },
    );
  });

  return Object.fromEntries(
    [...byKey]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, values]) => [
        key,
        [...values].sort((left, right) => left.localeCompare(right)),
      ]),
  );
};

export type CheckEntityPicker = {
  kindOptions: string[];
  typesByKind: Record<string, string[]>;
  lifecycleOptions: string[];
  ownerOptions: string[];
  tagOptions: string[];
  annotationOptions: Record<string, string[]>;
};

/**
 * Available entity kinds and, per kind, the entity types (`spec.type`) present
 * in the catalog, plus the lifecycle/owner/tag values and annotations a check
 * can be scoped by. Types are scoped by kind so the check form can offer only
 * the types relevant to the selected kinds. Entities themselves are searched on
 * demand by the dry-run picker, so they are not loaded here.
 */
export const useCheckEntityPicker = (): CheckEntityPicker => {
  const catalogApi = useApi(catalogApiRef);

  const { value } = useAsync(async () => {
    const kindResponse = await catalogApi.getEntityFacets({ facets: ['kind'] });
    const kindOptions = (kindResponse.facets.kind ?? [])
      .map(facet => facet.value)
      .filter(kind => CHECKABLE_KINDS.has(kind.toLowerCase()))
      .sort((left, right) => left.localeCompare(right));

    const typeEntries = await Promise.all(
      kindOptions.map(async kind => {
        const typeResponse = await catalogApi.getEntityFacets({
          facets: ['spec.type'],
          filter: { kind },
        });
        const types = (typeResponse.facets['spec.type'] ?? [])
          .map(facet => facet.value)
          .sort((left, right) => left.localeCompare(right));
        return [kind.toLowerCase(), types] as const;
      }),
    );

    // Scoped to checkable kinds so the form never offers a value that cannot
    // match any entity a check runs against.
    const facetResponse = await catalogApi.getEntityFacets({
      facets: [...SCOPE_FACETS],
      filter: { kind: kindOptions },
    });
    const valuesFor = (facet: string) =>
      (facetResponse.facets[facet] ?? [])
        .map(entry => entry.value)
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right));

    const annotationResponse = await catalogApi.getEntities({
      fields: ['metadata.annotations'],
      filter: { kind: kindOptions },
    });

    return {
      kindOptions,
      typesByKind: Object.fromEntries(typeEntries) as Record<string, string[]>,
      lifecycleOptions: valuesFor('spec.lifecycle'),
      ownerOptions: valuesFor('spec.owner'),
      tagOptions: valuesFor('metadata.tags'),
      annotationOptions: collectAnnotationOptions(annotationResponse.items),
    };
  }, [catalogApi]);

  return {
    kindOptions: value?.kindOptions ?? [],
    typesByKind: value?.typesByKind ?? {},
    lifecycleOptions: value?.lifecycleOptions ?? [],
    ownerOptions: value?.ownerOptions ?? [],
    tagOptions: value?.tagOptions ?? [],
    annotationOptions: value?.annotationOptions ?? {},
  };
};
